/**
 * UAT 2026-05-22 #2 — Task status pill + change dropdown for
 * TaskDetailSlideOver. The pill mirrors the live kanban column colour for
 * the task's current `taskStateId`; clicking opens a popover with every
 * column on the project. Selecting another column triggers the existing
 * `useTaskMutation({ type: 'state' })` path so optimistic update + rollback
 * + state_changes audit row stay identical to the board drag/drop flow.
 *
 * Read-only fallback: when `canManage` is false the pill renders as a
 * `<span>` (no dropdown). Mirrors the AssigneePicker chip vs picker split.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { BoardColumn } from '../../services/api/board';
import { useTaskMutation } from '../../hooks/useTaskMutation';
import { MtStatusPill } from '../mytasks/MtStatusPill';

export interface TaskStatusPickerProps {
  taskId: number;
  projectId: number;
  // Phase 51 INTAKE-02 (CF-2) — relaxed to number | null; triage tasks have
  // taskStateId=null. When null the picker shows the Triage column label read-only.
  currentStateId: number | null;
  columns: BoardColumn[];
  canManage: boolean;
  audience: 'internal' | 'external';
  onError?: (message: string) => void;
  /**
   * MKS-170 — which edge of the trigger the popover is anchored to.
   *
   * Defaults to 'right' because this component was written for
   * TaskDetailSlideOver (see the file header), which sits at the right of the
   * screen: anchoring right lets the 224px panel grow leftwards into open
   * space. ProjectListView reuses the picker as the second element of each
   * row, ~80px from the left edge, where that same anchoring pushed the panel
   * off-container and clipped every state name from the left ("cklog" for
   * "Backlog").
   *
   * The two call sites genuinely want opposite anchoring, so this belongs to
   * the caller. Do NOT flip the default to fix a list-view bug — that only
   * moves the clipping to the slide-over.
   */
  menuAlign?: 'left' | 'right';
}

export function TaskStatusPicker({
  taskId,
  projectId,
  currentStateId,
  columns,
  canManage,
  audience,
  onError,
  menuAlign = 'right',
}: TaskStatusPickerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mutation = useTaskMutation();

  const current = columns.find((c) => c.taskStateId === currentStateId) ?? null;

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleSelect = useCallback(
    (toStateId: number) => {
      if (toStateId === currentStateId) {
        setOpen(false);
        return;
      }
      mutation.mutate(
        {
          type: 'state',
          taskId,
          projectId: String(projectId),
          audience,
          toStateId,
        },
        {
          onError: (err) => {
            if (onError) onError(err.message ?? 'Server error');
          },
        },
      );
      setOpen(false);
    },
    [mutation, taskId, projectId, audience, currentStateId, onError],
  );

  // Read-only chip when the user has no rights to change status.
  if (!canManage) {
    return (
      <MtStatusPill
        status={current?.name ?? '—'}
        color={current?.color ?? null}
        className="shrink-0"
      />
    );
  }

  const isPending = mutation.isPending;

  return (
    <div ref={containerRef} className="relative shrink-0" data-testid="task-status-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={isPending}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Change status"
        className="inline-flex items-center gap-1 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:opacity-60"
        data-testid="task-status-picker-trigger"
      >
        <MtStatusPill
          status={current?.name ?? '—'}
          color={current?.color ?? null}
        />
        <ChevronDownIcon className="w-3.5 h-3.5 text-ms-text-muted" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Task status options"
          className={`absolute ${menuAlign === 'left' ? 'left-0' : 'right-0'} mt-1 w-56 max-h-72 overflow-y-auto rounded-lg border border-ms-border bg-white shadow-lg z-50 py-1`}
          data-testid="task-status-picker-popover"
        >
          {/* Phase 51 INTAKE-02 — exclude the synthetic Triage column (isTriage=true)
           * from the picker; triage tasks are moved via the Move to Open action,
           * not via the state picker. Only real state columns (taskStateId != null) appear. */}
          {columns.filter((col) => !col.isTriage).length === 0 ? (
            <div className="px-3 py-2 text-xs text-ms-text-muted italic">No states</div>
          ) : (
            columns.filter((col) => !col.isTriage && col.taskStateId !== null).map((col) => {
              const stateId = col.taskStateId as number; // null-filtered above
              const selected = stateId === currentStateId;
              return (
                <button
                  key={stateId}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => handleSelect(stateId)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-ms-canvas focus:bg-ms-canvas focus:outline-none"
                  data-testid={`task-status-option-${stateId}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      aria-hidden="true"
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: col.color }}
                    />
                    <span className="text-sm text-ms-text truncate">{col.name}</span>
                  </span>
                  {selected && (
                    <CheckIcon className="w-4 h-4 text-ms-text-secondary shrink-0" aria-hidden="true" />
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
